/** @jsxImportSource @emotion/react */
'use memo'

import React from 'react'
import css from '@emotion/css/macro'
import { DownOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  weightSemiBold,
  heading2,
  paragraphXSmall,
} from 'src/components/css'
import { CONTENT_GUTTER, MAX_CONTENT_WIDTH } from '../utils/constants'
import { ProfilePlayer } from '../types'
import ProfilePlayerActions from './ProfilePlayerActions'
import PlayerSwitchPanel from './PlayerSwitchModal'

const AVATAR_SIZE = 104
const PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

interface Props {
  player: ProfilePlayer | null
  players: ProfilePlayer[]
  selectedPlayerId: string | null
  onSelectPlayer: (player: ProfilePlayer) => void
  onEditPlayer: () => void
  onOpenFamily: () => void
  /** Current user is PRIMARY (admin) for the selected player → can edit it. */
  canEdit?: boolean
}

/**
 * Player header row for the player-based Profile page.
 * Shows the selected player's avatar, name (with switcher trigger), stats, and action buttons.
 */
const ProfilePlayerRow: React.FC<Props> = ({
  player,
  players,
  selectedPlayerId,
  onSelectPlayer,
  onEditPlayer,
  onOpenFamily,
  canEdit = false,
}) => {
  const [switcherOpen, setSwitcherOpen] = React.useState(false)
  const photoSrc = (player && getImageThumbnailId(player)) || PLACEHOLDER

  return (
    <div css={sectionStyle}>
      <div css={innerStyle}>
        {/* Avatar */}
        <img
          src={photoSrc}
          alt={
            player
              ? `${player.name_first ?? ''} ${player.name_last ?? ''}`.trim() ||
                'Player avatar'
              : 'Player avatar'
          }
          css={avatarStyle}
          onError={(e) => {
            const target = e.target as HTMLImageElement
            target.src = PLACEHOLDER
          }}
        />

        {/* Center column: label, name + switcher trigger */}
        <div css={centerColStyle}>
          <span css={captionLabelStyle}>
            {capitalizeWords('Player Profile')}
          </span>

          {player ? (
            <Dropdown
              open={switcherOpen}
              onOpenChange={setSwitcherOpen}
              trigger={['click']}
              placement='bottomLeft'
              // antd render-prop API — not a remounted component definition.
              // eslint-disable-next-line react/no-unstable-nested-components
              dropdownRender={() => (
                <PlayerSwitchPanel
                  players={players}
                  selectedPlayerId={selectedPlayerId}
                  onSelect={onSelectPlayer}
                  onClose={() => setSwitcherOpen(false)}
                />
              )}
            >
              <button
                type='button'
                css={nameRowStyle}
                aria-label='Switch player'
              >
                <span css={nameTextStyle}>
                  {player.name_first} {player.name_last}
                </span>
                <DownOutlined css={chevronStyle} />
              </button>
            </Dropdown>
          ) : (
            <span css={mutedNameStyle}>No player selected</span>
          )}
        </div>

        {/* Right column: player action buttons. Only shown when the user
            actually has a player — with no players there's nothing to edit. */}
        {player && (
          <div css={rightColStyle}>
            <ProfilePlayerActions
              onEditPlayer={onEditPlayer}
              onOpenFamily={onOpenFamily}
              player={player}
              canEdit={canEdit}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default ProfilePlayerRow

const sectionStyle = css`
  background-color: ${colors.SECONDARY_500};
  padding: 24px 0;
  width: 100%;
`

const innerStyle = css`
  max-width: ${MAX_CONTENT_WIDTH}px;
  margin: 0 auto;
  padding-left: ${CONTENT_GUTTER}px;
  padding-right: ${CONTENT_GUTTER}px;
  display: flex;
  align-items: center;
  gap: 16px;
`

const avatarStyle = css`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  border-radius: 80px;
  border: 1px solid ${(colors as Record<string, string>).NEXT_BADGE};
  object-fit: cover;
  flex-shrink: 0;
`

const centerColStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 8px;
  justify-content: center;
`

const captionLabelStyle = css`
  ${font.body}
  ${weightSemiBold}
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 1.44px;
`

const nameRowStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  /* Only take the space the name + chevron need, not the full column width. */
  align-self: flex-start;
  max-width: 100%;
`

const nameTextStyle = css`
  ${weightSemiBold}
  ${heading2}
  ${font.heading}
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  line-height: 120%;
  text-overflow: ellipsis;
`

const chevronStyle = css`
  font-size: 16px;
  color: ${colors.NEUTRAL_0};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  line-height: 0;
  margin-left: 5px;
  /* Nudge down 2px from center to match Figma (sits just below the text midline). */
  margin-top: 8px;
`

const mutedNameStyle = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 18px;
  color: ${colors.NEUTRAL_100};
`

const rightColStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
`
